import { buildProfileFields, isNamePlaceholder, type AdditionalField, type ProfileForCompletion } from '@/lib/profile/completion'
import type { RegistrationNeeds } from '@/lib/registration/client'
import type { HealthcareRole, SpecialtyOption } from '@/lib/specialties/types'
import { isValidE164 } from '@/lib/utils/phone'

export interface LiveField {
  field: AdditionalField
  required: boolean
}

const blank = (v: string | undefined): boolean => !v || v.trim() === ''

function parseIds(raw: string | undefined): string[] {
  try {
    const parsed = JSON.parse(raw || '[]')
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

/** The form's string values as the profile shape `buildProfileFields` reads. */
export function profileFromValues(values: Record<string, string>): ProfileForCompletion {
  const str = (k: string): string | null => (values[k] ? values[k] : null)
  return {
    first_name: str('first_name'),
    last_name: str('last_name'),
    patronymic: str('patronymic'),
    phone: str('phone'),
    date_of_birth: str('date_of_birth'),
    country: str('country'),
    region: str('region'),
    city: str('city'),
    address: str('address'),
    postal_code: str('postal_code'),
    specialty: str('specialty'),
    specialty_id: str('specialty_id'),
    secondary_specialty_ids: parseIds(values.secondary_specialty_ids),
    specialty_other: str('specialty_other'),
    healthcare_role: str('healthcare_role') as HealthcareRole | null,
    is_not_working: values.is_not_working === 'true',
    education: str('education'),
    education_other: str('education_other'),
    workplace: str('workplace'),
    position: str('position'),
  }
}

/**
 * Fields that only make sense together come along with each other: the role
 * with the specialty it asks for, the work fields with "not working", and the
 * country with its region (a new country must be able to clear the old
 * region). Adds them to `shown` and returns it.
 */
export function addCompanions(shown: Set<string>): Set<string> {
  if (shown.has('healthcare_role')) shown.add('specialty_id')
  if (shown.has('workplace') || shown.has('position')) {
    shown.add('is_not_working')
    shown.add('workplace')
    shown.add('position')
  }
  if (shown.has('country') || shown.has('region')) {
    shown.add('country')
    shown.add('region')
  }
  return shown
}

/**
 * The fields the form opens with, computed once when it opens. Only
 * required-and-missing fields open it; empty optional fields ride along
 * when it opens anyway (missing fields or ticket questions), never alone.
 */
export function initialShownFields(needs: RegistrationNeeds, specialties: SpecialtyOption[]): Set<string> {
  const shown = new Set<string>(needs.missingFields)

  if (shown.size > 0 || needs.questions.length > 0) {
    const asked = buildProfileFields(profileFromValues(needs.values), specialties, needs.states)
    const notWorking = needs.values.is_not_working === 'true'
    for (const field of asked) {
      const name = field.field_name
      // "Not working" is a switch on workplace/position, not an answer: its
      // false is '' here, so it would always look blank. It comes only as
      // their companion, never on its own.
      if (name === 'is_not_working') continue
      // Blank work fields of someone not working are waived, not unanswered:
      // the form would hide them again and leave the ticked switch alone.
      if (notWorking && (name === 'workplace' || name === 'position')) continue
      if (!field.is_required && blank(needs.values[name])) shown.add(name)
    }
  }

  return addCompanions(shown)
}

/** The fields on screen now, with requiredness that follows what the user has picked. */
export function liveFields(
  values: Record<string, string>,
  needs: RegistrationNeeds,
  specialties: SpecialtyOption[],
  shown: Set<string>
): LiveField[] {
  const notWorking = values.is_not_working === 'true'
  return buildProfileFields(profileFromValues(values), specialties, needs.states)
    .filter(field => {
      const name = field.field_name
      if (notWorking && (name === 'workplace' || name === 'position')) return false
      return shown.has(name) || (name === 'specialty_id' && shown.has('healthcare_role'))
    })
    .map(field => ({ field, required: field.is_required }))
}

/** field_name → translation key of what is wrong with it. */
export function validateProfile(live: LiveField[], values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {}
  for (const { field, required } of live) {
    const value = values[field.field_name]
    const missing = blank(value) || isNamePlaceholder(field.field_name, value)
    if (required && missing) errors[field.field_name] = 'additionalFields.validation.required'
    else if (field.field_name === 'phone' && !blank(value) && !isValidE164(value)) errors.phone = 'phone.invalid'
  }
  return errors
}

/** What to save: the live fields and their companions, never a field the form did not show. */
export function profileUpdateFor(live: LiveField[], values: Record<string, string>): Record<string, string> {
  const update: Record<string, string> = {}
  const put = (name: string) => { update[name] = (values[name] ?? '').trim() }
  for (const { field } of live) {
    const name = field.field_name
    put(name)
    if (name === 'education') put('education_other')
    // The form clears the region on a country change; save that even where the event does not ask for the region.
    if (name === 'country') put('region')
    if (name === 'specialty_id') {
      put('secondary_specialty_ids')
      put('specialty_other')
    }
  }
  return update
}
